import { useEffect, useMemo } from 'react'
import type {
    FormVariant,
    WorkflowIdentity,
} from '@/lib/order-workflow-tracking'
import { createOrderWorkflowTracker } from '@/lib/order-workflow-tracking'
import { posthog } from '@/lib/posthog'

/** The route owns this hook so responsive forms share one attempt. */
export function useOrderWorkflowTracking(
    identity: WorkflowIdentity,
    variant: FormVariant,
    prefilled: boolean,
) {
    const { actorId, impersonating, identityKey } = identity
    const tracker = useMemo(
        () =>
            createOrderWorkflowTracker(
                { actorId, impersonating, identityKey },
                variant,
                prefilled,
                {
                    capture: (event, props) => {
                        posthog.capture(event, props)
                    },
                    // Variant/prefill changes do not create a new route attempt.
                },
            ),
        [actorId, impersonating, identityKey],
    )
    useEffect(() => {
        tracker.setVariant(variant)
    }, [tracker, variant])
    useEffect(() => {
        tracker.start()
        const update = () =>
            tracker.setForeground(
                document.visibilityState === 'visible' && document.hasFocus(),
            )
        const pause = () => tracker.setForeground(false)
        update()
        window.addEventListener('focus', update)
        window.addEventListener('blur', pause)
        window.addEventListener('pagehide', pause)
        document.addEventListener('visibilitychange', update)
        const heartbeat = window.setInterval(() => tracker.observe(), 1000)
        return () => {
            window.clearInterval(heartbeat)
            window.removeEventListener('focus', update)
            window.removeEventListener('blur', pause)
            window.removeEventListener('pagehide', pause)
            document.removeEventListener('visibilitychange', update)
            // Cleanup also runs in StrictMode: only observed navigation is terminal.
            tracker.setForeground(false)
        }
    }, [tracker])
    return tracker
}
